import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Value } from 'slate';
import dotProp from 'dot-prop-immutable';
import { v4 as uuid } from 'uuid';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from '@reach/router';
import { Edit, Close, FileCopy } from '@material-ui/icons';
import axiosUI from '../../axiosUI';
import cx from 'classnames';
import Sidebar from '../Sidebar/Sidebar';
import Topbar from '../Topbar/Topbar';
import Loader from '../Loader/Loader';
import Main from '../Main/Main';
import Button from '../Button/Button';
import { withGlobalContext } from '../../App';
import FloatingTabs from './FloatingTabs';
import PopupWrapper from '../PopupWrapper';
import withCommonUtils from '../withCommonUtils';
import ChannelCard from './ChannelCard';
import NoData from '../NoData';
import ResourceStatePill from '../ResourceStatePill';
import ChannelOrder from './ChannelOrder';
import { ChannelHistory } from './ChannelHistory';
import serializer from '../Editor/serializer';
import {
  NODES,
  CHANNEL_TYPES,
  CHANNEL_VARIANTS,
  RESOURCE_STATES,
  OPERATION_TYPES,
  CONTEXT_FIELD_TYPE,
  NOTIFICATIONS,
  CONTEXT_MAP,
  CONTEXT_GROUPS,
} from '../../constants';
import MarketSwitcher from '../MarketSwitcher';
import iconsList from '../../helpers/getIconList';
const { UNSAVED, PUBLISHED, DRAFT } = RESOURCE_STATES;
const { OFFICIAL, OVERRIDE } = CHANNEL_VARIANTS;
const { INCLUDE, EXCLUDE } = CONTEXT_GROUPS;

class ChannelingPerMarket extends Component {
  static propTypes = {
    country: PropTypes.string,
    language: PropTypes.string,
    createNotification: PropTypes.func,
    navigate: PropTypes.func,
    getNewOperationType: PropTypes.func,
    hasDrafts: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      selectedChannelType: CHANNEL_TYPES.PHONE,
      [CHANNEL_TYPES.PHONE]: [],
      [CHANNEL_TYPES.CHAT]: [],
      [CHANNEL_TYPES.RASA_CHAT]: [],
      [CHANNEL_TYPES.EMAIL]: [],
      [CHANNEL_TYPES.CHATBOT]: [],
      hasDrafts: false,
      showChannelConfigWithId: '',
      showVariantType: '',
      openingHours: [],
      apiData: [],
      showUnsavedChangesWarningPopup: false,
      loading: false,
      contextOptions: {
        [CONTEXT_MAP.ARTICLE_SLUG]: [],
      },
      showChannelOrderDrawer: false,
    };
  }

  componentDidMount = () => {
    this.setState({ loading: true }, async () => {
      try {
        await this.fetchChannelsAndInitializeState();
      } catch (err) {
        console.log(err);
        this.props.createNotification({
          message: `Failed to fetch changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });

    // no need to block user activity for this call
    this.fetchSlugsForContext();
  };

  componentDidUpdate = async (prevProps) => {
    if (
      this.props.country !== prevProps.country ||
      this.props.language !== prevProps.language
    ) {
      this.setState({ loading: true }, async () => {
        try {
          await this.fetchChannelsAndInitializeState();
        } catch (err) {
          console.log(err);
          this.props.createNotification({
            message: `Failed to fetch changes`,
            type: NOTIFICATIONS.TYPE.ERROR,
            timeout: 3000,
          });
        }

        this.setState({ loading: false });
      });

      this.fetchSlugsForContext();
    }
  };

  fetchSlugsForContext = async () => {
    try {
      const slugsUrl = `/api/content-hub/slugs`;
      const { data } = await axiosUI.get(slugsUrl, {
        params: {
          salesChannel: this.props.country,
          locale: this.props.language,
        },
      });
      this.setState({
        contextOptions: {
          [CONTEXT_MAP.ARTICLE_SLUG]: data.map((slug) => {
            return {
              label: slug,
              value: slug,
            };
          }),
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  fetchChannelsAndInitializeState = async () => {
    const channelUrl = `/api/channels`;
    const openingHourUrl = `/api/opening-hours`;
    const commonParams = {
      salesChannel: this.props.country,
      locale: this.props.language,
    };

    const [channelResponse, openingHourResponse] = await Promise.all([
      axiosUI.get(channelUrl, { params: commonParams }),
      axiosUI.get(openingHourUrl, {
        params: {
          ...commonParams,
          state: PUBLISHED,
        },
      }),
    ]);
    const apiData = channelResponse.data;
    const hasDrafts = this.props.hasDrafts(apiData);
    const draftsMap = apiData.reduce((map, channel) => {
      if (channel.state === DRAFT) {
        map[`${channel.id}_${channel.variant}`] = true;
      }
      return map;
    }, {});

    // remove published item if its draft version is present
    const filteredEntries = apiData.filter((channel) => {
      if (
        channel.state === PUBLISHED &&
        draftsMap[`${channel.id}_${channel.variant}`]
      ) {
        return false;
      }
      return true;
    });

    const transformedData = filteredEntries.reduce((map, channel) => {
      map[channel.id] = map[channel.id] || {};

      const updatedChannel =
        this.transformChannelDataComingFromBackend(channel);
      const { id, name, type, locale, sales_channel_id, online, ...restKeys } =
        updatedChannel;

      map[updatedChannel.id] = {
        ...map[updatedChannel.id],
        id,
        name,
        type,
        locale,
        sales_channel_id,
        onlineState: online,
        [updatedChannel.variant]: { online, ...restKeys },
      };
      return map;
    }, {});

    const channelTypeBuckets = Object.keys(transformedData).reduce(
      (map, channelId) => {
        const channelGroup = transformedData[channelId];
        map[channelGroup.type].push(channelGroup);
        return map;
      },
      { PHONE: [], CHAT: [], EMAIL: [], RASA_CHAT: [], CHATBOT: [] }
    );

    this.setState({
      ...channelTypeBuckets,
      hasDrafts,
      apiData,
      openingHours: openingHourResponse.data.map((obj) => ({
        label: obj.name,
        value: obj.id,
      })),

      // close all selected items
      showChannelConfigWithId: '',
      showVariantType: '',
    });
  };

  toggleUnsavedChangesWarningPopup = () => {
    this.setState((prevState) => ({
      showUnsavedChangesWarningPopup: !prevState.showUnsavedChangesWarningPopup,
    }));
  };

  toggleChannelOrderDrawer = () => {
    this.setState((prevState) => ({
      showChannelOrderDrawer: !prevState.showChannelOrderDrawer,
    }));
  };

  transformChannelDataComingFromBackend = (channel) => {
    // ensure that this operation is immutable
    // otherwise you're doomed

    // deserialize html to slate object
    const primaryDescription = serializer.deserialize(
      channel.primary_description
    );
    const secondaryDescription = serializer.deserialize(
      channel.secondary_description
    );

    return {
      ...channel,
      primary_description: primaryDescription,
      secondary_description: secondaryDescription,
      [INCLUDE]: this.transformContextsComingFromBackend(channel[INCLUDE]),
      [EXCLUDE]: this.transformContextsComingFromBackend(channel[EXCLUDE]),
      contexts: undefined,
    };
  };

  updateState = (updateObject) => {
    this.setState(updateObject);
  };

  transformContextsComingFromBackend = (contextArray) => {
    const contextsMap = contextArray.reduce((contextGroup, context) => {
      const { key, value } = context;
      const isMultiSelect = CONTEXT_FIELD_TYPE[context.key] === 'multi_select';
      contextGroup[context.key] = contextGroup[context.key] || {
        id: context.id,
        key,
        value: isMultiSelect ? [] : '',
      };
      if (isMultiSelect) {
        contextGroup[context.key].value.push(value);
      } else {
        contextGroup[context.key].value = value;
      }

      return contextGroup;
    }, {});

    return Object.keys(contextsMap).map((key) => contextsMap[key]);
  };

  onTabChange = (event) => {
    this.setState({
      selectedChannelType: event.target.value,
      showChannelConfigWithId: '',
      showVariantType: '',
    });
  };

  getDefaultEditorValue = () => {
    const defaultValueJson = {
      document: {
        nodes: [
          {
            object: 'block',
            type: NODES.PARAGRAPH,
            nodes: [
              {
                object: 'text',
                leaves: [
                  {
                    text: ' ',
                  },
                ],
              },
            ],
          },
        ],
      },
    };
    return Value.fromJSON(defaultValueJson);
  };

  getVariantSpecificFieldsWithDefaultValues = () => {
    return {
      display_name: '',
      online: true,
      primary_description: this.getDefaultEditorValue(),
      secondary_description: this.getDefaultEditorValue(),
      channel_down_text: '',
      channel_button_text: '',
      destination: '',
      opening_hour_id: null,
      [INCLUDE]: [],
      [EXCLUDE]: [],
      operation_type: OPERATION_TYPES.CREATE,
      state: UNSAVED,
      show_offline_state: true,
    };
  };

  addNewChannel = () => {
    const { selectedChannelType } = this.state;
    const newChannel = {
      id: uuid(),
      name: selectedChannelType,
      type: selectedChannelType,
      locale: this.props.language,
      sales_channel_id: this.props.country,
      [OFFICIAL]: {
        ...this.getVariantSpecificFieldsWithDefaultValues(),
        variant: OFFICIAL,
      },
    };

    this.setState({
      [selectedChannelType]: [newChannel, ...this.state[selectedChannelType]],
    });
  };

  copyChannel = (originalChannel) => {
    const { type } = originalChannel;
    const newChannel = {
      ...originalChannel,
      id: uuid(),
      name: `${originalChannel.name}_COPY`,
      [OFFICIAL]: {
        ...originalChannel[OFFICIAL],
        operation_type: OPERATION_TYPES.CREATE,
        state: UNSAVED,
      },
    };
    if (OVERRIDE in originalChannel) {
      newChannel[OVERRIDE] = {
        ...originalChannel[OVERRIDE],
        operation_type: OPERATION_TYPES.CREATE,
        state: UNSAVED,
      };
    }
    this.setState({
      [type]: [newChannel, ...this.state[type]],
    });
  };

  showChannelConfig = (newId) => {
    this.setState((prevState) => {
      return {
        showChannelConfigWithId:
          prevState.showChannelConfigWithId === newId ? '' : newId,
        showVariantType: '',
      };
    });
  };

  updateChannelField = (value, target, channelIndex) => {
    const { selectedChannelType, showVariantType } = this.state;
    const currentChannel =
      this.state[selectedChannelType][channelIndex][showVariantType];

    const updatedChannelList = dotProp.set(
      this.state[selectedChannelType],
      target,
      value
    );
    updatedChannelList[channelIndex][showVariantType].operation_type =
      this.props.getNewOperationType(currentChannel);
    updatedChannelList[channelIndex][showVariantType].state = UNSAVED;

    this.setState({
      [selectedChannelType]: updatedChannelList,
    });
  };

  onInputFieldChange = (event, channelIndex) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    const name = event.target.name;
    this.updateChannelField(value, name, channelIndex);
  };

  onContextTypeSelect = (value, name, channelIndex) => {
    const { selectedChannelType } = this.state;
    const newContextKey = value;
    let newContextValue = '';
    const currentContext = dotProp.get(this.state[selectedChannelType], name);
    if (newContextKey) {
      const newContextValueFieldType = CONTEXT_FIELD_TYPE[newContextKey];
      newContextValue = newContextValueFieldType === 'multi_select' ? [] : '';
    }

    const updatedContext = {
      ...currentContext,
      key: newContextKey,
      value: newContextValue,
    };

    this.updateChannelField(updatedContext, name, channelIndex);
  };

  onVariantSwitch = (newVariant) => {
    return this.setState((prevState) => {
      return {
        showVariantType:
          prevState.showVariantType === newVariant ? '' : newVariant,
      };
    });
  };

  createOverrideVariant = (channelIndex) => {
    const { selectedChannelType } = this.state;
    const currentChannel = this.state[selectedChannelType][channelIndex];
    const updatedChannel = dotProp.set(currentChannel, OVERRIDE, {
      ...this.getVariantSpecificFieldsWithDefaultValues(),
      variant: OVERRIDE,
    });

    this.setState({
      [selectedChannelType]: dotProp.set(
        this.state[selectedChannelType],
        channelIndex,
        updatedChannel
      ),
    });
  };

  addNewContext = (channelIndex, contextType) => {
    const { selectedChannelType, showVariantType } = this.state;
    const currentChannel = this.state[selectedChannelType][channelIndex];
    const currentChannelContexts = currentChannel[showVariantType][contextType];

    const newContext = {
      id: uuid(),
      key: null,
      value: null,
    };

    const updatedChannel = dotProp.set(
      currentChannel,
      `${showVariantType}.${contextType}`,
      [newContext, ...currentChannelContexts]
    );
    updatedChannel[showVariantType].operation_type =
      this.props.getNewOperationType(currentChannel[showVariantType]);
    updatedChannel[showVariantType].state = UNSAVED;

    this.setState({
      [selectedChannelType]: dotProp.set(
        this.state[selectedChannelType],
        channelIndex,
        updatedChannel
      ),
    });
  };

  removeContext = (channelIndex, contextIndex, contextType) => {
    const { selectedChannelType, showVariantType } = this.state;
    const currentChannel = this.state[selectedChannelType][channelIndex];
    const currentChannelContexts = currentChannel[showVariantType][contextType];

    const updatedChannel = dotProp.set(
      currentChannel,
      `${showVariantType}.${contextType}`,
      [
        ...currentChannelContexts.slice(0, contextIndex),
        ...currentChannelContexts.slice(contextIndex + 1),
      ]
    );

    updatedChannel[showVariantType].operation_type =
      this.props.getNewOperationType(currentChannel[showVariantType]);
    updatedChannel[showVariantType].state = UNSAVED;

    this.setState({
      [selectedChannelType]: dotProp.set(
        this.state[selectedChannelType],
        channelIndex,
        updatedChannel
      ),
    });
  };

  submitChanges = () => {
    this.setState({ loading: true }, async () => {
      const { selectedChannelType } = this.state;
      const flattenedList = this.state[selectedChannelType].reduce(
        (list, channelGroup) => {
          const { OFFICIAL, OVERRIDE, ...commonFields } = channelGroup;
          const officialChannel = { ...OFFICIAL, ...commonFields };
          list.push(officialChannel);

          if (OVERRIDE) {
            const overrideChannel = { ...OVERRIDE, ...commonFields };
            list.push(overrideChannel);
          }

          return list;
        },
        []
      );

      const transformedList = flattenedList.map((channel) => {
        // serialize slate object to HTML
        channel.primary_description = serializer.serialize(
          channel.primary_description
        );
        channel.secondary_description = serializer.serialize(
          channel.secondary_description
        );

        channel[INCLUDE] = this.getFlattenedContextsForSubmission(
          channel[INCLUDE]
        );
        channel[EXCLUDE] = this.getFlattenedContextsForSubmission(
          channel[EXCLUDE]
        );

        return channel;
      });

      const unsavedChanges = transformedList.filter((channel) => {
        return channel.state === UNSAVED;
      });

      try {
        const url = '/api/channels';
        await axiosUI.post(url, unsavedChanges);

        this.props.createNotification({
          message: `Your changes are saved as draft`,
          type: NOTIFICATIONS.TYPE.SUCCESS,
          timeout: 5000,
        });

        this.fetchChannelsAndInitializeState();
      } catch (err) {
        console.log(err);

        this.props.createNotification({
          message: `Failed to save changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  getFlattenedContextsForSubmission = (contextArray) => {
    // flatten out contexts
    return contextArray.reduce((list, context) => {
      const { key, value, ...restParams } = context;

      // value can be either string -> in case of free text
      // or an object -> in case single select
      // or an array of objects -> in case of multi select
      if (typeof value === 'string') {
        list.push({ ...restParams, key, value });
      } else if (Array.isArray(value)) {
        const flattenedContext = value.map((currentValue) => ({
          ...restParams,
          key,
          value: currentValue,
        }));
        list = [...list, ...flattenedContext];
      }

      return list;
    }, []);
  };

  navigateToChangesetPage = () => {
    this.props.navigate('changeset');
  };

  getCombinedState = (channel) => {
    // if the channel has no override then just show
    // official variants' state
    if (!channel[OVERRIDE]) {
      return channel[OFFICIAL].state;
    }

    if (channel[OFFICIAL].state === channel[OVERRIDE].state) {
      return channel[OFFICIAL].state;
    }

    if (
      channel[OFFICIAL].state === UNSAVED ||
      channel[OVERRIDE].state === UNSAVED
    ) {
      return UNSAVED;
    }

    return;
  };

  resetSelectedChannel = () => {
    this.setState({ showChannelConfigWithId: '' });
  };

  render() {
    const { selectedChannelType, showChannelConfigWithId } = this.state;
    const selectedChannelHasSomeData = Boolean(
      this.state[selectedChannelType].length
    );
    const currentChannelTypeList = this.state[selectedChannelType];
    const flatCurrentChannelTypeList = currentChannelTypeList.reduce(
      (list, channel) => {
        list.push(channel[OFFICIAL]);

        if (channel[OVERRIDE]) {
          list.push(channel[OVERRIDE]);
        }

        return list;
      },
      []
    );
    const hasUnsavedChanges = flatCurrentChannelTypeList.some(
      (channel) => channel.state === UNSAVED
    );

    return (
      <Fragment>
        <Sidebar highlightNavItemIndex={3} />
        <Topbar className="market_banners__topbar flex justify-between items-center">
          <span className="font-bold text-lg capitalize">
            <Link
              className="bb-1-blue no-underline text-blue mr-2"
              to="/channeling">
              Channeling
            </Link>{' '}
            /
            <MarketSwitcher {...this.props} basePath="/channeling" />
          </span>
          <span className="float-right text-right">
            <Link
              to="opening-hours"
              className="bb-1-blue no-underline text-blue text-sm tracking-wide font-medium">
              Opening Hours
            </Link>
            <Button
              className="h-8 ml-8"
              onClick={this.toggleChannelOrderDrawer}>
              Channel Order
            </Button>
          </span>
        </Topbar>
        <Main className="market_channels__main overflow-x-hidden">
          <Loader loading={this.state.loading}>
            <div className="market_channels__main__content relative max-w-lg m-auto">
              <FloatingTabs
                selectedChannelType={selectedChannelType}
                onTabChange={this.onTabChange}
              />
              <div className="selected_channel_content bg-white px-4">
                <div
                  className={cx('flex items-center justify-end py-4', {
                    higlight_channel_actions: Boolean(showChannelConfigWithId),
                  })}>
                  <Button className="py-2" onClick={this.addNewChannel}>
                    Add new Channel
                  </Button>
                  {this.state.hasDrafts && (
                    <PopupWrapper
                      className="relative"
                      isOpen={this.state.showUnsavedChangesWarningPopup}
                      togglePopup={this.toggleUnsavedChangesWarningPopup}>
                      <Button
                        type="blue"
                        className="h-8 ml-8"
                        onClick={
                          hasUnsavedChanges
                            ? this.toggleUnsavedChangesWarningPopup
                            : this.navigateToChangesetPage
                        }>
                        Review Pending Draft
                      </Button>
                      <AnimatePresence>
                        {this.state.showUnsavedChangesWarningPopup && (
                          <motion.div
                            initial={{ opacity: 0, y: '80%', bottom: '-10px' }}
                            animate={{ opacity: 1, y: '100%' }}
                            exit={{ opacity: 0, y: '80%' }}
                            transition={{
                              ease: [0.18, 0.89, 0.39, 1.4],
                              duration: 0.3,
                            }}
                            className="button-popup absolute pin-r px-2 py-4 bg-white shadow-md rounded text-left w-64">
                            <div className="text-center leading-normal">
                              There are some unsaved changes. Are you sure you
                              want to leave this page?
                            </div>
                            <div className="flex justify-around mt-8">
                              <Button
                                className="h-8"
                                onClick={this.toggleUnsavedChangesWarningPopup}>
                                No
                              </Button>
                              <Button
                                type="red-transparent"
                                className="h-8"
                                onClick={this.navigateToChangesetPage}>
                                Yes, leave this page
                              </Button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </PopupWrapper>
                  )}
                  <Button
                    type="green"
                    onClick={this.submitChanges}
                    className="py-2 ml-8">
                    Save as Draft
                  </Button>
                  <span className="float-right"></span>
                </div>
                {selectedChannelHasSomeData ? (
                  <ul className="list-reset m-0 py-8">
                    {this.state[selectedChannelType].map(
                      (channel, channelIndex) => {
                        const showCurrentConfig =
                          showChannelConfigWithId === channel.id;
                        const channelCommonCardProps = {
                          channel,
                          channelIndex,
                          selectedChannelType,
                          openingHours: this.state.openingHours,
                          onInputFieldChange: this.onInputFieldChange,
                          updateChannelField: this.updateChannelField,
                          showVariantType: this.state.showVariantType,
                          onVariantSwitch: this.onVariantSwitch,
                          addNewContext: this.addNewContext,
                          removeContext: this.removeContext,
                          onContextTypeSelect: this.onContextTypeSelect,
                          updateParentState: this.updateState,
                          apiData: this.state.apiData,
                          transformChannelDataComingFromBackend:
                            this.transformChannelDataComingFromBackend,
                          currentChannelTypeList:
                            this.state[selectedChannelType],
                          createOverrideVariant: this.createOverrideVariant,
                          iconsList,
                        };
                        return (
                          <li
                            key={channel.id}
                            className={cx('mb-4', {
                              'bb-2-dashed-midGrey': !showCurrentConfig,
                              shadow: showCurrentConfig,
                              expanded_channel: showCurrentConfig,
                            })}>
                            <div className="channel_title flex items-center justify-between p-2">
                              <div>
                                <span
                                  className={cx('channel_state', {
                                    channel_state_online: Boolean(
                                      channel.onlineState
                                    ),
                                    channel_state_offline: Boolean(
                                      !channel.onlineState
                                    ),
                                  })}>
                                  ●
                                </span>
                                {channel.name}
                              </div>
                              <div className="flex items-center">
                                <ResourceStatePill
                                  state={this.getCombinedState(channel)}
                                />
                                <ChannelHistory
                                  channelId={channel.id}
                                  createNotification={
                                    this.props.createNotification
                                  }
                                />
                                <Button
                                  onClick={() => this.copyChannel(channel)}
                                  type="transparent"
                                  className="p-0-important shadow-none-important h-6 ml-8"
                                  title="Copy">
                                  <FileCopy
                                    className="text-blue"
                                    title="Copy"
                                  />
                                </Button>
                                <Button
                                  onClick={() =>
                                    this.showChannelConfig(channel.id)
                                  }
                                  type="transparent"
                                  className="p-0-important shadow-none-important h-6 ml-8">
                                  {showCurrentConfig ? (
                                    <Close className="text-darkGrey" />
                                  ) : (
                                    <Edit className="text-blue" />
                                  )}
                                </Button>
                              </div>
                            </div>
                            {showCurrentConfig && (
                              <ul className="channel_content list-reset m-0 p-4">
                                <li>
                                  <ChannelCard
                                    {...this.props}
                                    {...channelCommonCardProps}
                                    currentVariant={OFFICIAL}
                                    contextOptions={this.state.contextOptions}
                                  />
                                </li>
                                {channel[OVERRIDE] && (
                                  <li>
                                    <ChannelCard
                                      {...this.props}
                                      {...channelCommonCardProps}
                                      currentVariant={OVERRIDE}
                                      contextOptions={this.state.contextOptions}
                                    />
                                  </li>
                                )}
                              </ul>
                            )}
                          </li>
                        );
                      }
                    )}
                  </ul>
                ) : (
                  <div className="illustration_wrapper py-8 text-center">
                    <NoData />
                  </div>
                )}
              </div>
              {Boolean(this.state.showChannelConfigWithId) && (
                <Button
                  onClick={this.resetSelectedChannel}
                  type="transparent"
                  className="reset_selected_channel fixed z-20 pin-t pin-r p-0-important shadow-none-important mt-2 mr-2">
                  <Close className="text-white" fontSize="large" />
                </Button>
              )}
            </div>
          </Loader>
          <AnimatePresence>
            {this.state.showChannelOrderDrawer && (
              <ChannelOrder
                {...this.props}
                dissmissDrawer={this.toggleChannelOrderDrawer}
              />
            )}
          </AnimatePresence>
        </Main>
      </Fragment>
    );
  }
}

export default  withCommonUtils(withGlobalContext(ChannelingPerMarket));
